import React, { useState, useRef } from 'react';
import PassengersFilter from '~/components/filter/passengersFilter';
import DateFilter from '~/components/filter/dateFilter';
import DurationFilter from '~/components/filter/durationFilter';
import styled from 'styled-components';
import { useOnClickOutside } from '~/hooks/useOnClickOutside';
import ShowMapToggle from '~/components/filter/showMapToggle';
import { useRouter } from 'next/router';
import MoreFilters from '~/components/filter/moreFilters';
import moment from 'moment';
import { Trans, withI18n } from '@lingui/react';
import { t } from '@lingui/macro';

interface Props {
  mapFullScreen: boolean;
  showMap: boolean;
  setForceShowMapByButton: any;
  forceShowMapByButton: boolean;
  setShowMap: any;
  i18n: any;
  togleShowMap: any;
}
const BoatsFilter = ({
  showMap,
  setShowMap,
  mapFullScreen,
  i18n,
  forceShowMapByButton,
  setForceShowMapByButton,
  togleShowMap,
}: Props) => {
  const [showPassengersFilter, setShowPassengersFilter] = useState(false);
  const [showMoreFilters, setShoMoreFilters] = useState(false);
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [showDurationFilter, setShowDurationFilter] = useState(false);

  const passengersRef = useRef();
  const moreFiltersRef = useRef();
  const dateRef = useRef();
  const durationRef = useRef();

  const router = useRouter();

  useOnClickOutside(passengersRef, () => {
    setShowPassengersFilter(false);
  });
  useOnClickOutside(moreFiltersRef, () => {
    setShoMoreFilters(false);
  });
  useOnClickOutside(dateRef, () => {
    setShowDateFilter(false);
  });
  useOnClickOutside(durationRef, () => {
    setShowDurationFilter(false);
  });

  const toggleDurationFilter = () => {
    setShoMoreFilters(false);
    setShowDateFilter(false);
    setShowPassengersFilter(false);
    setShowDurationFilter((prev) => !prev);
  };

  const togglePassengersFilter = () => {
    setShowDurationFilter(false);
    setShoMoreFilters(false);
    setShowDateFilter(false);
    setShowPassengersFilter((prev) => !prev);
  };

  const toggleMoreFilters = () => {
    setShowDurationFilter(false);
    setShowPassengersFilter(false);
    setShowDateFilter(false);
    setShoMoreFilters((prev) => !prev);
  };

  const toggleDateFilter = () => {
    setShowDurationFilter(false);
    setShoMoreFilters(false);
    setShowPassengersFilter(false);
    setShowDateFilter((prev) => !prev);
  };

  const getDateFilterHeading = () => {
    if (router.query.availableFrom === undefined) {
      return <Trans id="boatsFilter.date" />;
    } else {
      return moment(router.query.availableFrom).format('MMM DD');
    }
  };

  const getPassengersFilterHeading = () => {
    if (router.query.passengerCount === undefined) {
      return <Trans id="boat.passengers" />;
    } else {
      return `${router.query.passengerCount} ${i18n._(t`boat.passengers`)}`;
    }
  };

  const getDurationFilterHeading = () => {
    if (router.query.duration === undefined) {
      return <Trans id="global.duration" />;
    } else {
      return `${i18n._(t`global.duration`)} (${JSON.parse(String(router.query.duration)).length})`;
    }
  };

  const getMoreFiltersHeading = () => {
    let counter = 0;
    if (router.query.boatSizeFrom !== undefined || router.query.boatSizeTo !== undefined) {
      counter++;
    }
    if (router.query.priceFrom !== undefined || router.query.priceTo !== undefined) {
      counter++;
    }
    if (router.query.isInstantBookable !== undefined) {
      counter++;
    }

    return counter === 0 ? <Trans id="boatsFilter.filters" /> : `${i18n._(t`boatsFilter.filters`)} (${counter})`;
  };

  return (
    <>
      <FilterWrapper mapFullScreen={mapFullScreen}>
        <FilterItem ref={dateRef}>
          <FilterButton
            className={router.query.hasOwnProperty('availableFrom') ? 'active' : ''}
            onClick={toggleDateFilter}
          >
            {getDateFilterHeading()}
          </FilterButton>

          {showDateFilter && <DateFilter setShowDateFilter={setShowDateFilter} />}
        </FilterItem>
        <FilterItem ref={durationRef}>
          <FilterButton
            className={router.query.hasOwnProperty('duration') ? 'active' : ''}
            onClick={toggleDurationFilter}
          >
            {getDurationFilterHeading()}
          </FilterButton>
          {showDurationFilter && <DurationFilter setShowDurationFilter={setShowDurationFilter} />}
        </FilterItem>
        <FilterItem ref={passengersRef}>
          <FilterButton
            className={router.query.hasOwnProperty('passengerCount') ? 'active' : ''}
            onClick={togglePassengersFilter}
          >
            {getPassengersFilterHeading()}
          </FilterButton>
          {showPassengersFilter && <PassengersFilter setShowPassengersFilter={setShowPassengersFilter} />}
        </FilterItem>
        <FilterItem ref={moreFiltersRef}>
          <FilterButton
            className={
              router.query.hasOwnProperty('boatSizeFrom') ||
              router.query.hasOwnProperty('priceFrom') ||
              router.query.hasOwnProperty('isInstantBookable') ||
              router.query.hasOwnProperty('boatSizeTo') ||
              router.query.hasOwnProperty('priceTo')
                ? 'active noMargin'
                : 'noMargin'
            }
            onClick={toggleMoreFilters}
          >
            {getMoreFiltersHeading()}
          </FilterButton>
          {showMoreFilters && <MoreFilters setShoMoreFilters={setShoMoreFilters} />}
        </FilterItem>

        {!mapFullScreen && (
          <ShowMapToggle
            forceShowMapByButton={forceShowMapByButton}
            showMap={showMap}
            setShowMap={setShowMap}
            setForceShowMapByButton={setForceShowMapByButton}
            togleShowMap={togleShowMap}
          />
        )}
      </FilterWrapper>
    </>
  );
};

const FilterWrapper = styled.div`
  position: sticky;
  overflow: visible;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  z-index: 99;
  top: 60px;
  padding: 1em 3rem;
  margin-top: -60px;
  display: flex;
  flex-direction: row;
  align-items: center;
  background: white;
  justify-content: ${(props) => (props.mapFullScreen ? 'center' : 'flex-start')};

  padding: ${(props) => (props.mapFullScreen ? '1em' : '1em 20px 1em 3rem ')};
  border-bottom: 1px solid #dbdfe5;
`;

const FilterButton = styled.div`
  border-radius: 5px;
  position: relative;
  border: 1px solid #8e9697;
  padding: 0.8em 1em;
  text-align: center;
  color: #8e9697;
  background-color: #fff;
  font-size: 12px;
  line-height: 1;
  font-weight: 500;
  transition: all 0.3s ease;
  display: inline-block;
  max-height: 35px;
  margin-right: 1em;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;

  &:hover {
    border: 1px solid #8e9697;
    background-color: #8e9697;
    color: #fff;
  }

  &.active {
    background-color: #72d4ba;
    color: #001d58;
    border: 1px solid #72d4ba;
  }

  &.noMargin {
    margin-right: 0px;
  }

  @media (max-width: 420px) {
    margin-right: 10px;
  }
`;

const FilterItem = styled.div`
  display: flex;
  flex-direction: column;
`;

export default withI18n()(BoatsFilter);
